import React, { useEffect, useRef, useState } from 'react'
import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
} from '@mui/material'
import { Controller, useForm } from 'react-hook-form'
import { Price } from '@nxcr-org/web-api-interface/lib/billing_service_pb'
import {
  isValidAmount,
  isValidTerm,
  toCents,
  toEpoch,
  toLocalDateTime,
  validateDateRange,
} from './priceForm.utils'
import {
  getPriceLimitError, getPriceScheduleStatus, INCOMPLETE_PRICES_MESSAGE, PRICE_LIMIT_MESSAGE, ProgramPrice,
} from './programs.utils'

interface PriceFormProps {
  price?: Price
  programId: string
  open: boolean
  onClose: () => void
  onSave: (price: Price) => Promise<void>
  prices: ProgramPrice[]
}

interface PriceValues {
  reservationFee: string
  deposit: string
  startFee: string
  monthlyRent: string
  priceTermMonths: string
  fromDate: string
  toDate: string
  hidden: boolean
}

const moneyFields = [
  { name: 'reservationFee', label: 'Reservation fee ($)' },
  { name: 'deposit', label: 'Default Deposit ($)' },
  { name: 'startFee', label: 'Start fee ($)' },
  { name: 'monthlyRent', label: 'Monthly rent ($)' },
] as const

export const PriceForm = ({ price, prices, programId, open, onClose, onSave }: PriceFormProps) => {
  const [saveError, setSaveError] = useState('')
  const scheduleStatus = getPriceScheduleStatus(prices)
  const mounted = useRef(false)
  const saving = useRef(false)
  const saved = useRef(false)
  const initialDates = useRef({
    fromDate: {
      epoch: price ? price.getFromDate() : 0,
      local: toLocalDateTime(price ? price.getFromDate() : 0),
    },
    toDate: {
      epoch: price ? price.getToDate() : 0,
      local: toLocalDateTime(price ? price.getToDate() : 0),
    },
  })
  const dateEpoch = (name: 'fromDate' | 'toDate', value: string) =>
    toEpoch(value, initialDates.current[name])
  const {
    control,
    getValues,
    handleSubmit,
    reset,
    formState: { isDirty, isSubmitting },
  } = useForm<PriceValues>({
    defaultValues: {
      reservationFee: price ? (price.getReservationFee() / 100).toFixed(2) : '0.00',
      deposit: price ? (price.getDeposit() / 100).toFixed(2) : '500.00',
      startFee: price ? (price.getStartFee() / 100).toFixed(2) : '0.00',
      monthlyRent: price ? (price.getMonthlyRent() / 100).toFixed(2) : '0.00',
      priceTermMonths: String(price ? price.getPriceTermMonths() : 0),
      fromDate: initialDates.current.fromDate.local,
      toDate: initialDates.current.toDate.local,
      hidden: price ? price.getHidden() : false,
    },
  })

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  useEffect(() => {
    if (!open || (!isDirty && !isSubmitting)) return
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (saved.current) return
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', beforeUnload)
    return () => window.removeEventListener('beforeunload', beforeUnload)
  }, [open, isDirty, isSubmitting])

  const close = () => {
    if (saving.current || isSubmitting) return
    if (!saved.current && isDirty && !window.confirm('Discard unsaved price changes?')) return
    onClose()
  }

  const submit = async (values: PriceValues) => {
    if (saving.current) return
    const limitError = getPriceLimitError(prices, {
      id: price?.getId() || '',
      fromDate: dateEpoch('fromDate', values.fromDate),
      toDate: dateEpoch('toDate', values.toDate),
      hidden: values.hidden,
    })
    if (limitError) {
      setSaveError(limitError)
      return
    }
    const summary = [
      `${price ? 'Update' : 'Create'} price?`,
      ...moneyFields.map(({ name, label }) => `${label}: ${(toCents(values[name]) / 100).toFixed(2)}`),
      `Term: ${values.priceTermMonths} months. Hidden: ${values.hidden ? 'yes' : 'no'}.`,
      `Starts: ${values.fromDate || 'unbounded'}; ends: ${values.toDate || 'unbounded'} (local time).`,
    ].join('\n')
    if ((price || values.hidden) && !window.confirm(summary)) return
    saving.current = true
    setSaveError('')
    try {
      const updated = price ? Price.deserializeBinary(price.serializeBinary()) : new Price()
      if (price) updated.setId(price.getId())
      updated.setProgramId(price ? price.getProgramId() : programId)
      updated.setReservationFee(toCents(values.reservationFee))
      updated.setDeposit(toCents(values.deposit))
      updated.setStartFee(toCents(values.startFee))
      updated.setMonthlyRent(toCents(values.monthlyRent))
      updated.setPriceTermMonths(Number(values.priceTermMonths))
      updated.setFromDate(dateEpoch('fromDate', values.fromDate))
      updated.setToDate(dateEpoch('toDate', values.toDate))
      updated.setHidden(values.hidden)
      await onSave(updated)
      saved.current = true
      if (mounted.current) {
        reset(values)
        onClose()
      }
    } catch (error) {
      if (mounted.current) setSaveError(
        error instanceof Error && [PRICE_LIMIT_MESSAGE, INCOMPLETE_PRICES_MESSAGE].includes(error.message)
          ? error.message
          : 'Unable to save the price. Your changes are preserved. Please refresh pricing or try again.'
      )
    } finally {
      saving.current = false
    }
  }

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth disableEscapeKeyDown={isSubmitting}>
      <form onSubmit={handleSubmit(submit)} noValidate>
        <DialogTitle>{price ? 'Edit price' : 'Create price'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {scheduleStatus.exceedsLimit && <Alert severity="warning">{PRICE_LIMIT_MESSAGE} Hide or expire an existing price to resolve the conflict.</Alert>}
            {scheduleStatus.hasIncompletePrices && <Alert severity="warning">{INCOMPLETE_PRICES_MESSAGE}</Alert>}
            {saveError && (
              <Alert severity="error">{saveError}</Alert>
            )}
            {moneyFields.map(({ name, label }) => (
              <Controller
                key={name}
                name={name}
                control={control}
                rules={{
                  validate: (value) =>
                    isValidAmount(value, name === 'monthlyRent') ||
                    `Enter ${name === 'monthlyRent' ? 'a positive' : 'a nonnegative'} dollar amount with at most two decimal places (maximum $21,474,836.47)`,
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label={label}
                    required
                    fullWidth
                    disabled={isSubmitting || (!price && (name === 'reservationFee' || name === 'deposit'))}
                    slotProps={{ htmlInput: { inputMode: 'decimal' } }}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            ))}
            {price && <Controller
              name="priceTermMonths"
              control={control}
              rules={{
                validate: (value) =>
                  isValidTerm(value) ||
                  'Enter a whole number from 0 to 2147483647',
              }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="Term (months)"
                  required
                  disabled={isSubmitting}
                  slotProps={{ htmlInput: { inputMode: 'numeric' } }}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />}
            {(['fromDate', 'toDate'] as const).map((name) => (
              <Controller
                key={name}
                name={name}
                control={control}
                rules={{
                  validate: (value) => {
                    const from = dateEpoch('fromDate', name === 'fromDate' ? value : getValues('fromDate'))
                    const to = dateEpoch('toDate', name === 'toDate' ? value : getValues('toDate'))
                    return validateDateRange(from, to)
                  },
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label={name === 'fromDate' ? 'Start (local time)' : 'End (local time)'}
                    type="datetime-local"
                    disabled={isSubmitting}
                    slotProps={{ inputLabel: { shrink: true }, htmlInput: { step: '0.001' } }}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            ))}
            <Controller
              name="hidden"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  label="Hidden"
                  control={
                    <Checkbox
                      name={field.name}
                      inputRef={field.ref}
                      checked={field.value}
                      onBlur={field.onBlur}
                      onChange={(_, checked) => field.onChange(checked)}
                      disabled={isSubmitting}
                    />
                  }
                />
              )}
            />
            {isSubmitting && (
              <Stack direction="row" spacing={1} role="status" alignItems="center">
                <CircularProgress size={18} />
                <span>Saving price…</span>
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={isSubmitting}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={isSubmitting}>Save price</Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default PriceForm
